/**
 * SelectRenderer — top-level component for the basic SELECT renderer slice.
 * Dispatches each clause to its sub-renderer using the token-aware outline.
 * The CTE section sits above the main SELECT clauses; basic / joins / CTEs
 * are earlier slices, and subqueries / set ops / windows land in subsequent
 * ones.
 *
 * Subquery wiring (this slice): we compute the local FROM aliases via
 * `extractFromAliases` and merge them with any inherited `outerAliases` from a
 * parent SelectRenderer, then pass the merged list down to each clause
 * sub-renderer so embedded `<SubquerySection>`s can detect correlated outer
 * references. `drillTabId` flows through unchanged so any subquery's "focus"
 * affordance pushes a properly-attributed entry onto the VQE drill stack.
 */

import { useMemo } from 'react';

import type { ParseOptions, StatementInfo } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import type { DrillEntry } from '../../types';
import { CteAddMenu } from './CteAddMenu';
import { CteAddPopover } from './CteAddPopover';
import { CteSection } from './CteSection';
import { extractCtes } from './extractCtes';
import { extractFromAliases } from './extractFromAliases';
import type { SelectOutline } from './extractSelectOutline';
import { extractSelectOutline } from './extractSelectOutline';
import { extractSetOperatorBranches } from './extractSetOperatorBranches';
import { FilterPills } from './FilterPills';
import { buildCteNameEntries } from './fromCteRef';
import { FromSection } from './FromSection';
import { GroupBySection } from './GroupBySection';
import { ClauseAddRow } from './ClauseAddRow';
import { LimitOffsetPanel } from './LimitOffsetPanel';
import { MainQueryBoundary } from './MainQueryBoundary';
import { NamedWindowSection } from './NamedWindowSection';
import { OrderBySection } from './OrderBySection';
import { SelectList } from './SelectList';
import { SetOperatorBranches } from './SetOperatorBranches';
import { FULL_CANVAS_ROOT_CLASS } from '../common/canvasLayout';

export interface SelectRendererProps {
  readonly statement: StatementInfo;
  readonly bufferText: string;
  readonly options: ParseOptions;
  readonly onEditAsSql: () => void;
  readonly drillTabId?: string;
  readonly outerAliases?: readonly string[];
  readonly editContext?: VqeEditContext;
  readonly layout?: 'full' | 'compact';
}

export function SelectRenderer({
  statement,
  bufferText,
  options,
  onEditAsSql,
  drillTabId,
  outerAliases,
  editContext,
  layout,
}: SelectRendererProps) {
  // F45: memoize the three full token-walk parses so they only recompute when
  // their real inputs change. `statement.range` is keyed on its primitive
  // start/end offsets (not the object reference) because nested/drilled
  // SelectRenderer mounts synthesise a FRESH `range` object every render (see
  // `makeNestedStatement` / `makeNestedSubqueryStatement`); the offset values
  // are stable across renders for the same logical position, whereas the object
  // identity is not. `bufferText` (buffer source) and `options` (the memoized
  // `parseOptions`) are reference-stable across pure re-renders. Stabilising
  // `outline` in particular keeps the downstream `localAliases` useMemo alive.
  const rangeStart = statement.range.startOffset;
  const rangeEnd = statement.range.endOffset;
  const setOpsOutline = useMemo(
    () => extractSetOperatorBranches(bufferText, statement.range, options),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bufferText, rangeStart, rangeEnd, options],
  );
  const outline = useMemo(
    () => extractSelectOutline(bufferText, statement.range, options),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bufferText, rangeStart, rangeEnd, options],
  );
  const cteOutline = useMemo(
    () => extractCtes(bufferText, statement.range, options),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bufferText, rangeStart, rangeEnd, options],
  );
  const localAliases = useMemo(
    () => extractFromAliases(bufferText, outline, options),
    [bufferText, outline, options],
  );
  // Task cte-context (COM-05/06/08): the FROM-item CTE-ref badge's name-match
  // fallback needs the enclosing WITH clause's declared CTE names, case-folded
  // per engine identifier rules. Built once from the already-extracted
  // `cteOutline.bindings` (no re-parsing of the WITH clause) and threaded down
  // through FromSection → FromItemRow.
  const cteNames = useMemo(
    () => buildCteNameEntries(cteOutline.bindings, bufferText, options),
    [cteOutline.bindings, bufferText, options],
  );
  const mergedAliases = useMemo(
    () => (outerAliases && outerAliases.length > 0 ? [...outerAliases, ...localAliases] : localAliases),
    [outerAliases, localAliases],
  );
  // LimitOffsetPanel's row values are click-to-edit only once editing is
  // actually ready (schema snapshot present) — a bare `editContext` (which
  // `useVqeEditWiring` returns for every rendered statement, connected or
  // not) would light up LIMIT editing on a disconnected tab. Mirrors the
  // `editReady` gate every other clause sub-renderer computes internally;
  // LimitOffsetPanel has no internal gate of its own, so it's threaded here.
  const limitEditContext =
    editContext && editContext.service.getSchemaSnapshot() ? editContext : undefined;
  const isCompact = (layout ?? 'full') === 'compact';
  const rootClass = isCompact
    ? 'flex w-full flex-wrap items-start gap-2'
    : FULL_CANVAS_ROOT_CLASS;
  // F3: in a compact inline subquery with no WITH clause yet, the empty "With"
  // card is replaced by a kebab Add-CTE menu on the SELECT header. The compact
  // MAIN query mounts its own kebab in the popover header (CompactVqeContent),
  // NOT here, so this is gated to the compact (inline-subquery) layout only.
  const cteHeaderMenu =
    isCompact && editContext && !cteOutline.hasWith ? (
      <CteAddMenu editContext={editContext} label="Add CTE" />
    ) : undefined;
  // Full layout with no WITH clause yet: "Add CTE" sits on the FROM card's
  // action line (after Add join / Add cross-join) instead of floating above
  // the cards; once a CTE exists the WITH card takes over. A SELECT without a
  // FROM gets the empty "FROM — none" row, whose action line carries it too.
  const cteAddAction =
    !isCompact && !cteOutline.hasWith && editContext && editContext.service.getSchemaSnapshot() ? (
      <CteAddPopover editContext={editContext} />
    ) : undefined;

  if (setOpsOutline.hasSetOperators) {
    return (
      <div
        className={rootClass}
        data-testid="vqe-select-renderer"
        data-has-set-ops="true"
        data-set-op-branch-count={setOpsOutline.branches.length}
      >
        <SetOperatorBranches
          statement={statement}
          bufferText={bufferText}
          options={options}
          outline={setOpsOutline}
          onEditAsSql={onEditAsSql}
          drillTabId={drillTabId}
          outerAliases={outerAliases}
          editContext={editContext}
        />
      </div>
    );
  }
  const subqueryCtx = {
    statementId: statement.id,
    drillTabId: drillTabId ?? statement.id,
    outerAliases: localAliases,
    onEditAsSql,
    mergedAliases,
  } as const;
  return (
    <div
      className={rootClass}
      data-testid="vqe-select-renderer"
      data-distinct={outline.distinct ? 'true' : 'false'}
      data-has-with={cteOutline.hasWith ? 'true' : 'false'}
      data-has-set-ops="false"
    >
      <CteSection
        bufferText={bufferText}
        outline={cteOutline}
        options={options}
        statementId={statement.id}
        onEditAsSql={onEditAsSql}
        editContext={editContext}
        drillTabId={subqueryCtx.drillTabId}
        compact={isCompact}
      />

      {cteOutline.hasWith ? <MainQueryBoundary /> : null}

      <FromSection
        bufferText={bufferText}
        outline={outline}
        options={options}
        subqueryCtx={subqueryCtx}
        editContext={editContext}
        cteNames={cteNames}
        extraActions={cteAddAction}
      />
      <SelectList
        bufferText={bufferText}
        outline={outline}
        options={options}
        subqueryCtx={subqueryCtx}
        statementId={statement.id}
        editContext={editContext}
        compact={isCompact}
        headerMenu={cteHeaderMenu}
      />
      <FilterPills
        bufferText={bufferText}
        span={outline.where}
        options={options}
        kind="where"
        subqueryCtx={subqueryCtx}
        editContext={editContext}
        enableViewModes
        density="compact"
        hideEmpty
        // COM-34/35 — the RECEIVED `outerAliases` prop (this SelectRenderer's
        // own enclosing scope, threaded in by a drilled remount), NOT
        // `subqueryCtx.outerAliases` (THIS level's own aliases, used for
        // grandchild-correlation detection) — see FilterPills' prop doc.
        outerAliases={outerAliases}
      />
      <GroupBySection
        bufferText={bufferText}
        outline={outline}
        options={options}
        editContext={editContext}
        hideEmpty
      />
      <FilterPills
        bufferText={bufferText}
        span={outline.having}
        options={options}
        kind="having"
        subqueryCtx={subqueryCtx}
        editContext={editContext}
        enableViewModes
        density="compact"
        hideEmpty
      />
      <NamedWindowSection
        bufferText={bufferText}
        outline={outline}
        options={options}
        statementId={statement.id}
        editContext={editContext}
      />
      <OrderBySection
        bufferText={bufferText}
        outline={outline}
        options={options}
        editContext={editContext}
        hideEmpty
      />
      <LimitOffsetPanel
        bufferText={bufferText}
        outline={outline}
        options={options}
        editContext={limitEditContext}
        hideEmpty
      />
      {/* The empty WHERE / GROUP BY / HAVING / ORDER BY / LIMIT, as one row. */}
      <ClauseAddRow bufferText={bufferText} outline={outline} options={options} editContext={editContext} />
    </div>
  );
}

export interface SubqueryWiring {
  readonly statementId: string;
  readonly drillTabId: string;
  readonly outerAliases: readonly string[];
  readonly mergedAliases: readonly string[];
  readonly onEditAsSql: () => void;
  readonly onDrill?: (entry: DrillEntry) => void;
}

export type { SelectOutline };
